import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  color: 'white',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: '1rem',
  marginLeft: theme.spacing(3),
  '&.active': {
    color: '#ffaf00',
  },
  '&:hover': {
    color: '#ffaf00',
  },
}));

const Header = ({ customTitle, customSubtitle }) => {
  const { t } = useTranslation();
  const location = useLocation();

  const navItems = [
    { label: t('nav.home'), path: '/miami-lakes-home' },
    { label: t('nav.about'), path: '/miami-lakes-about' },
    { label: t('nav.transportation_master_plan'), path: '/miami-lakes-transportation-master-plan' },
    { label: t('nav.greenways'), path: '/miami-lakes-greenways-trails-master-plan' },
    { label: t('nav.complete_streets_plan'), path: '/miami-lakes-complete-streets-master-plan' },
    { label: t('nav.actions'), path: '/miami-lakes-actions' },
    { label: t('nav.public_input'), path: '/miami-lakes-public-input' },
    { label: t('nav.adopted_plan'), path: '/miami-lakes-adopted-plans' },
    { label: t('nav.news'), path: '/miami-lakes-news' },
  ];

  // Define custom titles for specific pages
  const getPageTitle = () => {
    // If customTitle prop is provided, use it
    if (customTitle) return customTitle;
    
    const path = location.pathname;
    
    // Special cases: About page
    if (path.includes('about')) {
      return t('header.about_title', 'Vision Zero is Actions');
    }
    
    // Special cases: Public Input page
    if (path.includes('public-input')) {
      return t('header.public_input_title', 'Community involvement helped shape each project');
    }
    
    // Default for all other pages
    return t('header.title', 'Miami Lakes is creating a safer, more connected future');
  };

  const getPageSubtitle = () => {
    // If customSubtitle prop is provided, use it
    if (customSubtitle) return customSubtitle;
    
    const path = location.pathname;
    
    // Special cases: About page
    if (path.includes('about')) {
      return t('header.about_subtitle', 'Safer Streets for Miami Lakes');
    }
    
    // Special cases: Public Input page
    if (path.includes('public-input')) {
      return t('header.public_input_subtitle', 'Review the summaries below to learn how public input guided the process');
    }
    
    // Default for all other pages
    return t('header.subtitle', 'for everyone on the move');
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 'auto', md: '50vh' },
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url('${process.env.PUBLIC_URL}/images/miami_lakes_header.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 0,
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.7)',
          },
        }}
      />

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 2, flex: 1 }}>
        {/* Navigation */}
        <AppBar position="static" color="transparent" elevation={0}>
          <Toolbar
            sx={{
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 2,
              px: 2,
            }}
          >
            {navItems.map((item) => (
              <StyledNavLink key={item.path} to={item.path}>
                {item.label}
              </StyledNavLink>
            ))}
          </Toolbar>
        </AppBar>

        {/* Images and Text */}
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: 4,
            color: 'white',
            textAlign: 'center',
            px: 2,
          }}
        >
          {/* Logo Row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: { xs: 2, sm: 8 },
              mb: 4,
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/vz_transparent.png`}
              alt="First"
              style={{
                width: '250px',
                height: 'auto',
                borderRadius: '8px',
              }}
            />
            <a href="https://external-site.com" target="_blank" rel="noopener noreferrer">
              <img
                src={`${process.env.PUBLIC_URL}/images/TOML-Vertical-2-819x1024.png`}
                alt="External Link"
                style={{
                  width: '100px',
                  height: 'auto',
                  borderRadius: '8px',
                }}
              />
            </a>
          </Box>

          {/* Header Text */}
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 500,
              maxWidth: '90%',
              mx: 'auto',
              lineHeight: 1.4,
            }}
          >
            {getPageTitle()}
            <br />
            {getPageSubtitle()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Header;